/**
 * ErrorBoundary — a reusable class component that catches render-time exceptions
 * in its subtree and shows a small recoverable panel instead of letting the error
 * propagate and unmount the whole React tree (which renders as a blank white page).
 *
 * React only supports error boundaries as class components (getDerivedStateFromError
 * / componentDidCatch). Wrap any risky view, e.g. the Knowledge Graph Design wizard
 * phases, so a bad data shape degrades to a contained error + Retry rather than a
 * blank screen.
 */

import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import Button from './Button';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional custom fallback. Receives the error and a reset callback. */
  fallback?: (error: Error, reset: () => void) => React.ReactNode;
  /** Optional label shown in the default fallback ("…rendering {label}"). */
  label?: string;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught a render error:', error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (error) {
      if (this.props.fallback) return this.props.fallback(error, this.reset);
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-16 px-6 text-center">
          <div className="grid h-12 w-12 place-items-center rounded-full bg-red-50 text-red-500">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-neutral-900">
              Something went wrong rendering this view
            </h3>
            <p className="mt-1 max-w-md text-sm text-neutral-500">
              {this.props.label ? `An error occurred while rendering ${this.props.label}. ` : ''}
              You can retry, or go back and try again.
            </p>
            {error.message && (
              <p className="mt-2 max-w-md break-words font-mono text-xs text-neutral-400">{error.message}</p>
            )}
          </div>
          <Button variant="outline" size="sm" onClick={this.reset}>
            <RefreshCw className="mr-1 h-4 w-4" />
            Retry
          </Button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
